// How an Artemis reply reads in the panel: labels, step icons and the map-changes card. Pure,
// so the components stay thin and this stays testable.

import { isObjectCreatingTool } from './tools'
import { huntDataCard, needsHuntData } from './hunt-cards'
import { weatherCard, weatherObservationCard } from './weather-card'
import { unitCard } from './unit-cards'
import { deerActivityDays } from './deer-card'
import { savedSkillFrom } from './skills'
import { savedAlertFrom } from './alerts'

const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`

// m:ss
export const clockLabel = ms => {
  const secs = Math.max(0, Math.round((ms || 0) / 1000))
  return `${Math.floor(secs / 60)}:${String(secs % 60).padStart(2, '0')}`
}

const secondsBetween = (from, to) => (from != null && to != null ? Math.max(1, Math.round((to - from) / 1000)) : null)

// Reasoning and skills aren't tools: a skill is how Artemis goes about it (API-3685).
const toolSteps = steps => steps.filter(s => s.kind !== 'thinking' && s.kind !== 'skill')

// The one line the steps fold into once the answer starts or the reply ends.
export const stepsSummary = (message, now = Date.now()) => {
  const steps = message.steps || []
  const tools = toolSteps(steps).length || (message.tools || []).length
  const streaming = message.status === 'streaming'
  if (streaming) return tools ? `Used ${plural(tools, 'tool')} · writing…` : 'Writing…'

  if (steps.length === 1 && steps[0].kind !== 'thinking' && steps[0].title) {
    return steps[0].summary ? `${steps[0].title} · ${steps[0].summary}` : steps[0].title
  }
  const secs = secondsBetween(message.startedAt, message.answerStartedAt || message.endedAt || now)
  const thought = secs ? `thought for ${secs}s` : null
  if (!tools) return thought ? `Thought for ${secs}s` : 'Thought it through'
  return [`Used ${plural(tools, 'tool')}`, thought].filter(Boolean).join(' · ')
}

export const stepCountLabel = steps => plural(steps.length, 'step')

// The skills a reply used, for its method badge (API-3685): [{ key, title }] from load_skill steps
// that succeeded, once each. Title is capitalised by the server; Name is the fallback.
export const skillsUsed = message => {
  const seen = new Set()
  return (message.steps || []).filter(s => s.kind === 'skill' && s.status === 'succeeded').map(s => {
    const result = s.result && s.result.Kind === 'skill' ? s.result : {}
    const name = result.Name || s.stepId
    const raw = result.Title || (result.Name ? String(result.Name).replace(/-/g, ' ') : null)
    const title = raw ? raw.charAt(0).toUpperCase() + raw.slice(1) : null
    // Mine: one of the user's own skills (API-3688), badged "Your skill".
    // id: the user's skill, to open it in the Skills section (WEB-7170).
    return { key: name, title, mine: result.Mine === true, ...(result.Mine === true && result.Id != null ? { id: result.Id } : {}) }
  }).filter(s => s.title && !seen.has(s.key) && seen.add(s.key))
}

// Which icon and colour a step row gets. tone: 'think', 'reg', 'map' (the default green),
// 'failed' or 'running'. Tool names are only used for the picture, never to decide behaviour.
export const stepVisual = step => {
  let icon = 'map'
  if (step.kind === 'thinking') icon = 'think'
  else if (step.kind === 'skill') icon = 'skill'
  else if (step.kind === 'search') icon = 'search'
  else {
    const tool = step.tool || ''
    if (tool === 'create_marker') icon = 'pin'
    else if (tool === 'create_shape') icon = 'shape'
    else if (/layer/.test(tool)) icon = 'layer'
    else if (/regulation|application|draw|season|hunt/.test(tool)) icon = 'reg'
    else if (/search|document/.test(tool)) icon = 'search'
  }
  let tone = icon === 'think' ? 'think' : icon === 'reg' ? 'reg' : 'map'
  if (step.status === 'failed') { icon = 'failed'; tone = 'failed' }
  if (step.status === 'started') tone = 'running'
  return { icon, tone }
}

// Chips naming what a search step found: the first two titles, then "+N".
export const stepChips = (step, tools = []) => {
  if (step.status !== 'succeeded') return []
  const tool = tools.find(t => t.toolUseId === step.stepId || (step.toolUseId && t.toolUseId === step.toolUseId))
  const docs = (tool && tool.documents) || []
  const chips = docs.slice(0, 2).map(d => d.title)
  if (docs.length > 2) chips.push(`+${docs.length - 2}`)
  return chips
}

const failedActionSub = {
  subscription_required: 'Needs a higher subscription · not turned on',
  invalid_input: 'A basemap can only be swapped for another',
  not_found: 'Not in your layer menu'
}

const failedActionLabel = ({ outcome, layerName }) => {
  if (outcome === 'subscription_required') return `${layerName} needs a subscription`
  if (outcome === 'invalid_input') return `Couldn't turn off ${layerName}`
  return `Couldn't find ${layerName}`
}

const REMOVABLE_SHAPES = ['line', 'polygon', 'circle']
const FEET_PER_METRE = 3.28084

// A create_marker / create_shape step's Result, for its row. key names it in message.removed.
export const savedObject = result => {
  if (!result || (result.Kind !== 'marker' && result.Kind !== 'shape')) return null
  const point = Array.isArray(result.Point) && result.Point.length === 2
  const bbox = Array.isArray(result.Bbox) && result.Bbox.length === 4
  const elevation = Number.isFinite(result.ElevationMeters)
    ? `${Math.round(result.ElevationMeters * FEET_PER_METRE).toLocaleString('en-US')} ft`
    : null
  return {
    key: `${result.Kind}:${result.Id}`,
    name: result.Name || (result.Kind === 'marker' ? 'Waypoint' : 'Shape'),
    meta: [result.Type, elevation].filter(Boolean).join(' · '),
    locatable: point || bbox,
    removable: !!result.Id && (result.Kind === 'marker' || REMOVABLE_SHAPES.includes(result.Type)),
    result
  }
}

// The "changes to your map" card. Rows:
//   { key, kind: 'layer', on, label, action } for layers the app changed (Undo / Redo)
//   { key, kind: 'view', undone, canUndo, label, sub, action } for map moves (set_map_view)
//   { key, kind: 'failed', label, sub, upgrade } for layer changes or map moves that didn't happen
//   { key, kind: 'saved', icon, label, sub, object, canShow, canRemove, removed } for waypoints and
//     shapes Artemis saved; object comes from the step's Result, when the server sent one
// notes: layers that were already as asked. active: rows that still stand.
// A saved reply's map changes that ran on someone else's map (a partner's, in a group) or before this
// page loaded: its succeeded set_layer_visibility and set_map_view steps, which this viewer can apply to
// their own map. Empty when this page ran the reply's actions itself (message.actions has them).
export const offeredChanges = message => {
  if ((message.actions || []).length > 0) return []
  return (message.steps || [])
    .filter(s => s.status === 'succeeded' && s.input && (s.tool === 'set_layer_visibility' || s.tool === 'set_map_view'))
    .map(s => s.tool === 'set_layer_visibility'
      ? { key: s.stepId, tool: s.tool, input: s.input, icon: 'layer', label: s.title || s.input.layer_name || 'A layer', button: s.input.visible ? 'Turn on' : 'Turn off' }
      : { key: s.stepId, tool: s.tool, input: s.input, icon: 'map', label: s.title || 'A map view', button: 'Go there' })
}

export const mapChanges = message => {
  const rows = [], notes = []
  ;(message.actions || []).forEach(action => {
    // Hunt Planner's guided intake: the state, species and hunt type Artemis picked.
    if (action.tool === 'set_planner_filters') {
      rows.push(action.outcome === 'applied'
        ? { key: action.toolUseId, kind: 'view', undone: false, canUndo: false, action, label: 'Set up the planner', sub: action.label || 'Hunt Planner' }
        : { key: action.toolUseId, kind: 'failed', label: "Couldn't set up the planner", sub: `The planner has no ${String(action.field || 'option').replace('_', ' ')} like that`, upgrade: false })
      return
    }
    if (action.tool === 'set_map_view') {
      if (action.outcome === 'moved') {
        rows.push({
          key: action.toolUseId,
          kind: 'view',
          undone: !!action.undone,
          // Undo needs where the map was; without it the row just says what happened.
          canUndo: !!action.previous,
          action,
          label: action.undone ? 'Map back where it was' : `Moved the map${action.label ? ` to ${action.label}` : ''}`,
          sub: action.undone ? (action.label ? `Was: ${action.label}` : 'Moved back') : 'Your view'
        })
      } else {
        rows.push({ key: action.toolUseId, kind: 'failed', label: "Couldn't move the map", sub: action.label || 'The place wasn\u2019t on the map', upgrade: false })
      }
      return
    }
    if (action.outcome === 'changed') {
      const on = !!action.visible
      rows.push({
        key: action.toolUseId,
        kind: 'layer',
        on,
        // "on" here is what Artemis asked for; after an Undo the layer is back where it was.
        asked: action.asked === undefined ? on : action.asked,
        action,
        label: ''
      })
    } else if (action.outcome === 'unchanged') {
      notes.push(`${action.layerName} was already ${action.visible ? 'on' : 'off'}.`)
    } else {
      rows.push({
        key: action.toolUseId,
        kind: 'failed',
        label: failedActionLabel(action),
        sub: failedActionSub[action.outcome] || 'Not changed',
        upgrade: action.outcome === 'subscription_required'
      })
    }
  })

  const steps = message.steps || []
  const removed = message.removed || []
  const saves = steps.length
    ? steps.filter(s => isObjectCreatingTool(s.tool) && s.status === 'succeeded')
      .map(s => ({ key: s.stepId, tool: s.tool, title: s.title, summary: s.summary, object: savedObject(s.result) }))
    : (message.tools || []).filter(t => isObjectCreatingTool(t.tool) && t.status === 'succeeded')
      .map(t => ({ key: t.toolUseId, tool: t.tool }))
  saves.forEach(save => {
    const shape = save.tool === 'create_shape'
    const object = save.object
    const gone = !!object && removed.includes(object.key)
    const name = object && object.name
    rows.push({
      key: save.key,
      kind: 'saved',
      icon: shape ? 'shape' : 'pin',
      removed: gone,
      object,
      canShow: !gone && !!object && object.locatable,
      canRemove: !gone && !!object && object.removable,
      label: gone
        ? `Removed “${name}”`
        : save.title || (name ? `Saved “${name}”` : shape ? 'Saved a shape' : 'Saved a waypoint'),
      sub: gone ? 'Removed from your map' : save.summary || (object && object.meta) || 'Saved to your map'
    })
  })

  const active = rows.filter(r => (r.kind === 'saved' && !r.removed) || (r.kind === 'layer' && r.on === r.asked) || (r.kind === 'view' && !r.undone)).length
  return { rows, notes, active }
}

export const layerRowLabel = row => {
  const name = row.action.layerName
  if (row.on === row.asked) return `Turned ${row.on ? 'on' : 'off'} ${name}`
  return `${name} turned back ${row.on ? 'on' : 'off'}`
}

export const changesLabel = active => `${plural(active, 'change')} to your map`

// Short basemap names for the composer chip, by layer id (see context.js).
export const basemapShortLabel = basemapLayerId => {
  if (basemapLayerId === 81) return 'SAT'
  if (basemapLayerId === 891) return 'HYB'
  return basemapLayerId == null ? null : 'TOPO'
}

// "Sees your view · SAT · 3 layers"
// onScratchpad: the full view's scratchpad map is what Artemis reads.
export const contextChipLabel = (basemapLayerId, overlayCount, onScratchpad = false) =>
  [onScratchpad ? 'Sees the scratchpad map' : 'Sees your view', basemapShortLabel(basemapLayerId), plural(overlayCount, 'layer')].filter(Boolean).join(' · ')

// For past chats: "3 questions · 2 map changes"
export const chatSubtitle = messages => {
  const changes = messages.reduce((n, m) => n + mapChanges(m).active, 0)
  return [plural(messages.length, 'question'), changes ? plural(changes, 'map change') : null].filter(Boolean).join(' · ')
}

// The staging diagnostics line under a reply, from done.Stats or a saved message's Stats:
// { model, modelId, parts: [...] }. Saved replies from before Stats was stored have zero timings,
// which mean unknown, so timings show only when TotalMs is set.
export const statsLine = (stats, cost) => {
  const n = v => (typeof v === 'number' && Number.isFinite(v) ? v : null)
  const count = v => n(v).toLocaleString('en-US')
  const parts = []
  if (stats) {
    if (n(stats.TotalMs) > 0) parts.push(`Vector ${n(stats.VectorMs) || 0}ms · Docs ${n(stats.DocumentMs) || 0}ms · LLM ${n(stats.ModelMs) || 0}ms · Total ${stats.TotalMs}ms`)
    if (n(stats.DocumentCount) != null && n(stats.TotalMs) > 0) parts.push(`${stats.DocumentCount} docs`)
    // ToolCalls is the list of tools called, e.g. ["get_regulations"]; a count is read too.
    const toolCalls = Array.isArray(stats.ToolCalls) ? stats.ToolCalls.length : n(stats.ToolCalls)
    const work = [n(stats.AgentRounds) > 0 && plural(stats.AgentRounds, 'round'), toolCalls > 0 && plural(toolCalls, 'tool call')].filter(Boolean)
    if (work.length) parts.push(work.join(' · '))
    if (n(stats.InputTokens) != null || n(stats.OutputTokens) != null) {
      const cached = n(stats.CacheReadTokens) > 0 ? ` (${count(stats.CacheReadTokens)} cached)` : ''
      parts.push(`${count(stats.InputTokens || 0)}↑${cached} ${count(stats.OutputTokens || 0)}↓ tokens`)
    }
  }
  const costText = costLabel(cost ?? (stats && stats.EstimatedCostUsd))
  if (costText) parts.push(costText)
  if (!stats && !costText) return null
  return { model: (stats && stats.ModelKey) || null, modelId: (stats && stats.ModelId) || null, parts }
}

// "≈ $0.0042", "≈ $0.123", "≈ $1.24": an estimated model cost (API-3693), or null.
export const costLabel = usd => {
  if (typeof usd !== 'number' || !Number.isFinite(usd) || usd < 0) return null
  const digits = usd < 0.01 ? 4 : usd < 1 ? 3 : 2
  return `≈ $${usd.toFixed(digits)}`
}

// "Just now", a time today, "Yesterday", or a date.
export const timeLabel = (at, now = Date.now()) => {
  if (!at) return ''
  if (now - at < 60 * 1000) return 'Just now'
  const day = d => new Date(d).toDateString()
  if (day(at) === day(now)) return new Date(at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
  if (day(at) === day(now - 24 * 60 * 60 * 1000)) return 'Yesterday'
  const sameYear = new Date(at).getFullYear() === new Date(now).getFullYear()
  return new Date(at).toLocaleDateString([], sameYear ? { month: 'short', day: 'numeric' } : { month: 'short', day: 'numeric', year: 'numeric' })
}

// Past chats grouped by when they were last active, for the history list: Today, Yesterday,
// Previous 7 days, Earlier. Keeps the chats' order; empty groups are left out.
const startOfDay = t => { const d = new Date(t); d.setHours(0, 0, 0, 0); return d.getTime() }
export const historyGroup = (at, now = Date.now()) => {
  const days = Math.round((startOfDay(now) - startOfDay(at || 0)) / DAY_MS)
  return days <= 0 ? 'Today' : days === 1 ? 'Yesterday' : days < 7 ? 'Previous 7 days' : 'Earlier'
}
export const groupChats = (chats, now = Date.now()) => chats.reduce((groups, chat) => {
  const label = historyGroup(chat.at, now)
  const group = groups.find(g => g.label === label)
  if (group) group.chats.push(chat)
  else groups.push({ label, chats: [chat] })
  return groups
}, [])

// The chats whose title or preview has every word of the query (case-insensitive).
export const matchChats = (chats, query) => {
  const words = (query || '').toLowerCase().split(/\s+/).filter(Boolean)
  if (words.length === 0) return chats
  return chats.filter(c => {
    const text = `${c.title || ''} ${c.preview || ''}`.toLowerCase()
    return words.every(w => text.includes(w))
  })
}

// Structured cards (API-3638) --------------------------------------------------------------

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

// "Sep 2" from YYYY-MM-DD, without the time zone moving the day.
export const shortDate = ymd => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(ymd || '')
  return m ? `${MONTHS[Number(m[2]) - 1]} ${Number(m[3])}` : ''
}

const dateRange = (start, end) => {
  const a = shortDate(start), b = shortDate(end)
  return a && b && a !== b ? `${a} – ${b}` : a || b
}

const licenseTag = type => {
  if (!type) return null
  const t = String(type).toLowerCase()
  if (t === 'otc') return { label: 'OTC', draw: false }
  if (t === 'draw') return { label: 'Draw', draw: true }
  return { label: String(type), draw: /draw/.test(t) }
}

const pageLabel = page => (page ? `p. ${page}` : null)
const MAX_HUNT_ROWS = 6

// A get_regulations Result as the Regulation card:
//   { title, hunts: [{ key, name, dates, tag }], moreHunts, rule, footer, bookletUrl, truncated }
export const regulationsCard = result => {
  if (!result || result.Kind !== 'regulations') return null
  const hunts = Array.isArray(result.Hunts) ? result.Hunts : []
  const rules = Array.isArray(result.Rules) ? result.Rules : []
  if (hunts.length === 0 && rules.length === 0) return null

  const title = [
    [result.State, result.Species].filter(Boolean).join(' '),
    result.Unit ? `Unit ${result.Unit}` : null,
    hunts.length ? 'open hunts' : 'rules'
  ].filter(Boolean).join(' · ')

  const rows = hunts.slice(0, MAX_HUNT_ROWS).map((hunt, i) => {
    const dates = Array.isArray(hunt.Dates) ? hunt.Dates : []
    const starts = dates.map(d => d.Start).filter(Boolean).sort()
    const ends = dates.map(d => d.End).filter(Boolean).sort()
    const methods = Array.isArray(hunt.Methods) ? hunt.Methods : []
    return {
      key: hunt.HuntCode || hunt.DrawCode || String(i),
      name: hunt.Season || methods.join(', ') || hunt.HuntCode || 'Hunt',
      dates: dateRange(starts[0], ends[ends.length - 1]),
      tag: licenseTag(hunt.LicenseType),
      url: hunt.Url || null
    }
  })

  const booklet = (Array.isArray(result.Booklets) && result.Booklets[0]) || null
  const rule = rules.find(r => r.Text) || null
  const ruleSource = rule && [rule.BookletTitle, pageLabel(rule.Page)].filter(Boolean).join(', ')
  const footer = [
    ruleSource || (booklet && [booklet.Title, booklet.Year].filter(Boolean).join(' ')),
    rules.length ? plural(rules.length, 'rule') : null
  ].filter(Boolean).join(' · ')

  return {
    title,
    hunts: rows,
    moreHunts: Math.max(0, hunts.length - rows.length),
    rule: rule ? rule.Text : null,
    footer,
    bookletUrl: (rule && rule.Url) || (booklet && booklet.Url) || null,
    truncated: !!result.Truncated
  }
}

const DAY_MS = 24 * 60 * 60 * 1000

// A milestone's moment: At is UTC ISO, Date is a calendar day (taken as its end, for "closes in").
const milestoneTime = m => {
  if (m.At) return Date.parse(m.At)
  const d = /^(\d{4})-(\d{2})-(\d{2})$/.exec(m.Date || '')
  return d ? Date.UTC(Number(d[1]), Number(d[2]) - 1, Number(d[3]), 23, 59) : NaN
}

const milestoneDate = (m, timeZone) => {
  if (m.Date) return shortDate(m.Date)
  if (!m.At) return ''
  try {
    return new Date(m.At).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZone: timeZone || undefined })
  } catch (e) {
    return new Date(m.At).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
  }
}

const zoneLabel = timeZone => {
  const names = { 'America/Denver': 'Mountain Time', 'America/Phoenix': 'Arizona Time', 'America/Boise': 'Mountain Time', 'America/Chicago': 'Central Time', 'America/Los_Angeles': 'Pacific Time', 'America/New_York': 'Eastern Time' }
  return names[timeZone] || timeZone || null
}

// A get_application_periods Result as Draw deadline cards, one per period:
//   { key, title, badge, milestones: [{ key, label, date, estimated, past }], footer, applyUrl }
export const applicationPeriodCards = (result, now = Date.now()) => {
  if (!result || result.Kind !== 'application_periods' || !Array.isArray(result.Periods)) return []
  return result.Periods.slice(0, 3).map((period, i) => {
    const milestones = (Array.isArray(period.Milestones) ? period.Milestones : []).map((m, j) => {
      const time = milestoneTime(m)
      return {
        key: m.Milestone || String(j),
        milestone: m.Milestone || '',
        label: m.Label || m.Milestone || '',
        date: milestoneDate(m, period.TimeZone),
        estimated: !!m.Estimated,
        past: Number.isFinite(time) && time < now,
        time
      }
    })
      // In date order; the server's order isn't. Undated ones go last.
      .sort((a, b) => (Number.isFinite(a.time) ? a.time : Infinity) - (Number.isFinite(b.time) ? b.time : Infinity))
    const close = milestones.find(m => /close|deadline/i.test(`${m.milestone} ${m.label}`) && !m.past && Number.isFinite(m.time))
    // Whole days left, so 7 days and 2 hours reads as 7; under a day is today.
    const days = close ? Math.floor((close.time - now) / DAY_MS) : null
    return {
      key: `${period.DrawName || 'draw'}-${period.Year || ''}-${i}`,
      title: [period.Species, period.DrawName, period.Residency].filter(Boolean).join(' · '),
      badge: days == null ? null : days <= 0 ? 'Closes today' : `Closes in ${plural(days, 'day')}`,
      milestones,
      footer: [zoneLabel(period.TimeZone) && `Times in ${zoneLabel(period.TimeZone)}`, period.Year].filter(Boolean).join(' · '),
      applyUrl: period.ApplyUrl || null
    }
  })
}

// The succeeded steps whose Result is a card, in order, then one upgrade card when a hunt data
// tool failed for want of a subscription.
export const resultCards = (message, now = Date.now()) => {
  const cards = []
  ;(message.steps || []).forEach(step => {
    if (step.status !== 'succeeded' || !step.result) return
    const reg = regulationsCard(step.result)
    if (reg) cards.push({ key: step.stepId, kind: 'regulations', card: reg })
    applicationPeriodCards(step.result, now).forEach(card => cards.push({ key: `${step.stepId}-${card.key}`, kind: 'draw', card }))
    const hunt = huntDataCard(step.result)
    if (hunt) cards.push({ key: step.stepId, ...hunt })
    const weather = weatherCard(step.result)
    if (weather) cards.push({ key: step.stepId, kind: 'weather_forecast', card: weather })
    const observed = weatherObservationCard(step.result)
    if (observed) cards.push({ key: step.stepId, kind: 'weather_observation', card: observed })
    const unit = unitCard(step.result)
    if (unit) cards.push({ key: step.stepId, ...unit })
    const deer = deerActivityDays(step.result)
    if (deer) cards.push({ key: step.stepId, kind: 'deer_activity', card: { days: deer } })
    const saved = savedSkillFrom(step.result)
    if (saved) cards.push({ key: step.stepId, kind: 'user_skill', card: saved })
    const alert = savedAlertFrom(step.result)
    if (alert) cards.push({ key: step.stepId, kind: 'alert', card: alert })
  })
  const locked = (message.steps || []).filter(needsHuntData)
  if (locked.length) cards.push({ key: 'upgrade', kind: 'upgrade', card: { tools: locked.map(s => s.title).filter(Boolean) } })
  return cards
}

const COMMUNITY_SOURCES = ['forum_thread', 'podcast_episode']

const timeLocator = secs => {
  const s = Math.max(0, Math.round(secs))
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), r = s % 60
  return h ? `${h}:${String(m).padStart(2, '0')}:${String(r).padStart(2, '0')}` : `${m}:${String(r).padStart(2, '0')}`
}

// The sources under an answer. With citations (API-3639) they are the citations, numbered to match
// the answer's [n] chips; otherwise the documents the searches returned. documentId, when a
// document matches, lets the user flag the source.
//   [{ key, n, title, url, sub, community, documentId, disliked }]
export const answerSources = message => {
  const documents = message.sources || []
  const citations = message.citations || []
  if (citations.length === 0) {
    return documents.map((doc, i) => ({
      key: doc.key || `id:${doc.documentId ?? i}`, n: i + 1, title: doc.title, url: doc.url || null,
      sub: null, community: false, documentId: doc.documentId, disliked: !!doc.disliked
    }))
  }
  return citations.map(c => {
    const doc = documents.find(d => d.url && (d.url === c.url || d.url === c.citeUrl))
    const locator = c.locator && (c.locator.page ? `p. ${c.locator.page}` : Number.isFinite(c.locator.start_time) ? timeLocator(c.locator.start_time) : null)
    return {
      key: `cite:${c.index}`,
      n: c.index,
      title: c.title || (doc && doc.title) || 'Source',
      url: c.citeUrl || c.url || null,
      sub: [locator, c.asOf ? `as of ${c.asOf}` : null].filter(Boolean).join(' · ') || null,
      community: COMMUNITY_SOURCES.includes(c.sourceType),
      documentId: doc ? doc.documentId : undefined,
      disliked: !!(doc && doc.disliked)
    }
  })
}
